// =============================================================================
// ui/tabs/SuppressedPositions.tsx — Settings → Trade Management → Unfinished
// Positions.
// -----------------------------------------------------------------------------
// The orphan positions the user has permanently dismissed — a position whose
// closing fill never arrived because the connection dropped, say. Once
// dismissed, the importer never warns about that one again.
//
// ── ⚠ WITHOUT THIS SCREEN THE LIST CAN ONLY GROW ──
//
// Dismissing is a one-way door reachable from an import warning; nothing in this
// client could show what had been dismissed, let alone undo it. A user who
// dismissed the wrong row had no way to find out and no way back — and the
// consequence is silent, because the symptom is a warning that never appears.
//
// ⚠ THE FINGERPRINT IS THE ONLY RECORD. Beta stores a pipe-joined string and
// nothing else, so the row is reconstructed by taking it apart. A fingerprint
// that will not parse still renders — as itself — because an unreadable entry is
// exactly the one someone needs to be able to delete.
// =============================================================================

import { useState } from 'react';
import { usePreferences } from '../state/preferences';
import { parseSuppressed } from '../../selectors/mistakeTags';
// ⚠ `num`, which is this client's `fmtNum` — same two decimals, same thousands
// separator, and the same em dash for a value that is not a number.
import { num } from '../format';

export function SuppressedPositions() {
  const { prefs, update } = usePreferences();
  const raw = prefs?.suppressedOpenPositions ?? [];
  const [confirming, setConfirming] = useState<string | null>(null);

  const remove = (fingerprint: string) => {
    setConfirming(null);
    // ⚠ FILTERED BY VALUE, not spliced by index. The rendered order is the
    // stored order today, but an index-based delete is one sort away from
    // removing the wrong row — and the rows all look alike.
    void update({
      suppressedOpenPositions: raw.filter((x) => String(x) !== fingerprint),
    });
  };

  return (
    <section className="card">
      <h3>Unfinished positions</h3>
      <p className="muted">
        Positions you dismissed permanently from an import warning. These no longer
        raise a warning when you import. Remove one to start being warned about it
        again.
      </p>

      {raw.length === 0 ? (
        <p className="muted">
          Nothing dismissed. Anything you dismiss from an unfinished-position
          warning shows up here so you can undo it.
        </p>
      ) : (
        <div className="supp-list">
          {raw.map((entry, i) => {
            const fp = String(entry);
            const p = parseSuppressed(entry);
            return (
              <div className="supp-row" key={`${fp}-${i}`}>
                <span className="grow supp-text">
                  {p && (p.contract || p.side || p.qty) ? (
                    <>
                      <b>{p.contract || '?'}</b> · {p.side || '?'} {p.qty || '?'}
                      {/* ⚠ FORMATTED, not the raw fingerprint field. The
                          fingerprint stores the fill price as it was written —
                          "5412.2500" — and printing that verbatim is four
                          trailing zeros of noise on a row whose whole job is to
                          be recognised at a glance. Beta formats it here:
                          `fmtNum(+entry, 2)`, index.html:50015. */}
                      {p.entry ? <> @ {num(Number(p.entry), 2)}</> : null}
                      {p.openTime ? <span className="muted"> · opened {p.openTime}</span> : null}
                      {p.account ? <span className="muted"> · {p.account}</span> : null}
                    </>
                  ) : (
                    // ⚠ Rendered verbatim rather than skipped. See the header.
                    <span className="mono">{fp}</span>
                  )}
                </span>
                {confirming === fp ? (
                  <>
                    <span className="muted supp-confirm">Warn about this again?</span>
                    <button type="button" className="btn btn-sm"
                      onClick={() => setConfirming(null)}>Cancel</button>
                    <button type="button" className="btn danger btn-sm"
                      onClick={() => remove(fp)}>Remove</button>
                  </>
                ) : (
                  <button
                    type="button" className="btn btn-sm"
                    aria-label={`Stop suppressing ${p?.contract || fp}`}
                    onClick={() => setConfirming(fp)}
                  >Remove</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
